import { apiClient } from '@/api/client';
import type {
  Workspace,
  WorkspaceMember,
  WorkspaceInvite,
  CreateWorkspaceData,
  UpdateWorkspaceData,
  CreateInviteData,
  CreateMemberData,
  UpdateMemberRoleData,
} from '../types/workspace.types';

// Workspace CRUD

export const fetchWorkspaces = async (): Promise<Workspace[]> => {
  const response = await apiClient.get<Workspace[]>('/workspaces');
  return response.data;
};

export const fetchWorkspace = async (id: number): Promise<Workspace> => {
  const response = await apiClient.get<Workspace>(`/workspaces/${id}`);
  return response.data;
};

export const createWorkspace = async (data: CreateWorkspaceData): Promise<Workspace> => {
  const response = await apiClient.post<Workspace>('/workspaces', data);
  return response.data;
};

export const updateWorkspace = async (
  id: number,
  data: UpdateWorkspaceData
): Promise<Workspace> => {
  const response = await apiClient.put<Workspace>(`/workspaces/${id}`, data);
  return response.data;
};

export const deleteWorkspace = async (id: number): Promise<void> => {
  await apiClient.delete(`/workspaces/${id}`);
};

// Members

export const fetchMembers = async (workspaceId: number): Promise<WorkspaceMember[]> => {
  const response = await apiClient.get<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`);
  return response.data;
};

export const updateMemberRole = async (
  workspaceId: number,
  userId: number,
  data: UpdateMemberRoleData
): Promise<WorkspaceMember> => {
  const response = await apiClient.patch<WorkspaceMember>(
    `/workspaces/${workspaceId}/members/${userId}`,
    data
  );
  return response.data;
};

export const removeMember = async (workspaceId: number, userId: number): Promise<void> => {
  await apiClient.delete(`/workspaces/${workspaceId}/members/${userId}`);
};

export const createMember = async (
  workspaceId: number,
  data: CreateMemberData
): Promise<WorkspaceMember> => {
  const response = await apiClient.post<WorkspaceMember>(
    `/workspaces/${workspaceId}/members`,
    data
  );
  return response.data;
};

// Invites

export const fetchInvites = async (workspaceId: number): Promise<WorkspaceInvite[]> => {
  const response = await apiClient.get<WorkspaceInvite[]>(`/workspaces/${workspaceId}/invites`);
  return response.data;
};

export const createInvite = async (
  workspaceId: number,
  data: CreateInviteData
): Promise<WorkspaceInvite> => {
  const response = await apiClient.post<WorkspaceInvite>(
    `/workspaces/${workspaceId}/invites`,
    data
  );
  return response.data;
};

export const revokeInvite = async (workspaceId: number, inviteId: string): Promise<void> => {
  await apiClient.delete(`/workspaces/${workspaceId}/invites/${inviteId}`);
};

export const resendInvite = async (
  workspaceId: number,
  inviteId: string
): Promise<WorkspaceInvite> => {
  const response = await apiClient.post<WorkspaceInvite>(
    `/workspaces/${workspaceId}/invites/${inviteId}/resend`
  );
  return response.data;
};

export const acceptInvite = async (code: string): Promise<WorkspaceMember> => {
  const response = await apiClient.post<WorkspaceMember>(`/workspaces/join/${code}`);
  return response.data;
};
